import React from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import PageHeader from './PageHeader';
import { Link } from 'react-router-dom';
import { APP_STORE_URL } from '../constants';

const FeaturesPage = () => {
    const { getFullTranslation } = useLanguage();
    const t = getFullTranslation('featuresPage');

    const featureSections = [
        {
            tag: t.aiRecognition,
            title: t.f1Title,
            desc: t.f1Desc,
            items: [t.f1List1, t.f1List2, t.f1List3, t.f1List4],
            icon: (
                <svg className="w-16 h-16 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
            ),
            reverse: false,
        },
        {
            tag: t.aiAdvisor,
            title: t.f2Title,
            desc: t.f2Desc,
            items: [t.f2List1, t.f2List2, t.f2List3, t.f2List4],
            icon: (
                <svg className="w-16 h-16 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
            ),
            reverse: true,
        },
        {
            tag: t.cloudSync,
            title: t.f3Title,
            desc: t.f3Desc,
            items: [t.f3List1, t.f3List2, t.f3List3, t.f3List4],
            icon: (
                <svg className="w-16 h-16 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                </svg>
            ),
            reverse: false,
        },
        {
            tag: t.premium,
            title: t.f4Title,
            desc: t.f4Desc,
            items: [t.f4List1, t.f4List2, t.f4List3, t.f4List4],
            icon: (
                <svg className="w-16 h-16 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
            ),
            reverse: true,
        },
    ];

    return (
        <div>
            <PageHeader title={t.title} subtitle={t.subtitle} />

            <div className="max-w-5xl mx-auto px-6 py-16">
                {featureSections.map((feature, index) => (
                    <div key={index} className={`grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-16 ${feature.reverse ? 'md:direction-rtl' : ''}`}>
                        <div className={feature.reverse ? 'md:order-1' : ''}>
                            <span className="inline-block bg-orange-50 text-orange-500 px-3 py-1 rounded-full text-xs font-semibold mb-3">{feature.tag}</span>
                            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{feature.title}</h2>
                            <p className="text-gray-500 text-base mb-5">{feature.desc}</p>
                            <ul className="space-y-3">
                                {feature.items.map((item, i) => (
                                    <li key={i} className="flex items-center text-gray-700">
                                        <span className="inline-flex items-center justify-center w-6 h-6 bg-orange-500 text-white rounded-full text-xs font-bold mr-3 flex-shrink-0">✓</span>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className={`bg-gradient-to-br from-orange-50 to-orange-100 rounded-3xl p-12 flex items-center justify-center min-h-[300px] ${feature.reverse ? 'md:order-0' : ''}`}>
                            <div className="w-28 h-28 bg-white rounded-3xl shadow-lg shadow-orange-200 flex items-center justify-center">
                                {feature.icon}
                            </div>
                        </div>
                    </div>
                ))}

                {/* CTA */}
                <div className="bg-gradient-to-br from-[#163020] to-[#0f1f15] rounded-3xl p-12 md:p-16 text-center text-white mt-10">
                    <h2 className="text-2xl md:text-3xl font-bold mb-4">{t.ctaTitle}</h2>
                    <p className="opacity-80 text-base mb-8">{t.ctaDesc}</p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center px-8 py-3.5 bg-orange-500 text-white font-semibold rounded-xl hover:bg-orange-600 transition">{t.downloadBtn}</a>
                        <Link to="/subscription" className="inline-flex items-center px-8 py-3.5 bg-white/10 text-white font-semibold rounded-xl border border-white/20 hover:bg-white/20 transition">{t.viewPlan}</Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FeaturesPage;
